import { AudioWaveform, Axis3D, ChartLine, CheckSquare, CircleSlash2, Cuboid, Dices, Infinity as InfinityIcon, LineChart, ListOrdered, MoveUpRight, Percent, ShieldCheck, Tangent } from 'lucide-vue-next';
import { experimentConfig as advancedMath1ExperimentConfig } from '@/components/subject/advanced-math-1/experimentConfig.ts';
import { homeConfig as advancedMath1HomeConfig } from '@/components/subject/advanced-math-1/homeConfig.ts';
import { experimentConfig as advancedMath2ExperimentConfig } from '@/components/subject/advanced-math-2/experimentConfig.ts';
import { homeConfig as advancedMath2HomeConfig } from '@/components/subject/advanced-math-2/homeConfig.ts';

import { experimentConfig as analyticGeometryExperimentConfig } from '@/components/subject/analytic-geometry/experimentConfig';
import { homeConfig as analyticGeometryHomeConfig } from '@/components/subject/analytic-geometry/homeConfig';
import { experimentConfig as derivativesExperimentConfig } from '@/components/subject/derivatives/experimentConfig.ts';
import { homeConfig as derivativesHomeConfig } from '@/components/subject/derivatives/homeConfig.ts';
import { experimentConfig as functionsExperimentConfig } from '@/components/subject/functions/experimentConfig';
import { homeConfig as functionsHomeConfig } from '@/components/subject/functions/homeConfig';
import { experimentConfig as linearAlgebraEnExperimentConfig } from '@/components/subject/linear-algebra-en/experimentConfig';
import { homeConfig as linearAlgebraEnHomeConfig } from '@/components/subject/linear-algebra-en/homeConfig';
import { experimentConfig as linearAlgebraExperimentConfig } from '@/components/subject/linear-algebra/experimentConfig.ts';
import { homeConfig as linearAlgebraHomeConfig } from '@/components/subject/linear-algebra/homeConfig.ts';
import { experimentConfig as logicalReasoningExperimentConfig } from '@/components/subject/logical-reasoning/experimentConfig';
import { homeConfig as logicalReasoningHomeConfig } from '@/components/subject/logical-reasoning/homeConfig';
import { experimentConfig as numberTheoryExperimentConfig } from '@/components/subject/number-theory/experimentConfig.ts';
import { homeConfig as numberTheoryHomeConfig } from '@/components/subject/number-theory/homeConfig.ts';
import { experimentConfig as planeVectorsExperimentConfig } from '@/components/subject/plane-vectors/experimentConfig';
import { homeConfig as planeVectorsHomeConfig } from '@/components/subject/plane-vectors/homeConfig';
import { experimentConfig as probabilityStatisticsExperimentConfig } from '@/components/subject/probability-statistics/experimentConfig.ts';
import { homeConfig as probabilityStatisticsHomeConfig } from '@/components/subject/probability-statistics/homeConfig.ts';
import { experimentConfig as probabilityExperimentConfig } from '@/components/subject/probability/experimentConfig.ts';
import { homeConfig as probabilityHomeConfig } from '@/components/subject/probability/homeConfig.ts';
import { experimentConfig as riskTheoryExperimentConfig } from '@/components/subject/risk-theory/experimentConfig.ts';
import { homeConfig as riskTheoryHomeConfig } from '@/components/subject/risk-theory/homeConfig.ts';
import { experimentConfig as sequencesSeriesExperimentConfig } from '@/components/subject/sequences-series/experimentConfig.ts';
import { homeConfig as sequencesSeriesHomeConfig } from '@/components/subject/sequences-series/homeConfig.ts';
import { experimentConfig as setTheoryExperimentConfig } from '@/components/subject/set-theory/experimentConfig';
import { homeConfig as setTheoryHomeConfig } from '@/components/subject/set-theory/homeConfig';

import { experimentConfig as solidGeometryExperimentConfig } from '@/components/subject/solid-geometry/experimentConfig';
import { homeConfig as solidGeometryHomeConfig } from '@/components/subject/solid-geometry/homeConfig';
import { experimentConfig as statisticsExperimentConfig } from '@/components/subject/statistics/experimentConfig.ts';
import { homeConfig as statisticsHomeConfig } from '@/components/subject/statistics/homeConfig.ts';

import { experimentConfig as trigonometryExperimentConfig } from '@/components/subject/trigonometry/experimentConfig';
import { homeConfig as trigonometryHomeConfig } from '@/components/subject/trigonometry/homeConfig';

interface Subject {
  id: SubjectId;
  id_: string;
  name: string;
  subjectName: string;
  description: string;
  color: string;
  teacher: string;
  icon: any;
}

type SubjectId = 'probability' | 'probability-bilingual' | 'advanced-math-1' | 'advanced-math-2' | 'linear-algebra' | 'number-theory' | 'statistics' | 'set-theory' | 'solid-geometry' | 'analytic-geometry' | 'trigonometry' | 'plane-vectors' | 'functions' | 'logical-reasoning' | 'sequences-series' | 'derivatives' | 'probability-statistics' | 'risk-theory' | 'linear-algebra-en';

const subjectConfig: Record<SubjectId, Subject> = {
  'advanced-math-1': {
    id: 'advanced-math-1',
    id_: '2',
    name: '高等数学（上）',
    subjectName: '高等数学（上）',
    description: '微积分，函数与极限的艺术，适合喜欢挑战和推理的你',
    color: '#171717',
    teacher: '北邮高数上团队',
    icon: InfinityIcon,
  },
  'advanced-math-2': {
    id: 'advanced-math-2',
    id_: '3',
    name: '高等数学（下）',
    subjectName: '高等数学（下）',
    description: '微积分，函数与极限的艺术，适合喜欢挑战和推理的你',
    color: '#171717',
    teacher: '北邮高数下团队',
    icon: InfinityIcon,
  },
  'linear-algebra': {
    id: 'linear-algebra',
    id_: '7',
    name: '线性代数',
    subjectName: '线性代数',
    description: '空间与向量的语言，适合喜欢抽象思维和结构分析的你',
    color: '#171717',
    teacher: '北邮线代团队',
    icon: MoveUpRight,
  },
  'linear-algebra-en': {
    id: 'linear-algebra-en',
    id_: '24',
    name: '双语线性代数 / Linear Algebra',
    subjectName: '双语线性代数',
    description: '线性代数的英文版本，适合喜欢英文中学习空间与向量的你',
    color: '#171717',
    teacher: '北邮双语线代团队',
    icon: MoveUpRight,
  },
  'probability': {
    id: 'probability',
    id_: '5',
    name: '概率论',
    subjectName: '概率论',
    description: '探索不确定世界的规律，适合喜欢逻辑和建模的你',
    color: '#171717',
    teacher: '北邮概率团队',
    icon: Dices,
  },
  'probability-bilingual': {
    id: 'probability-bilingual',
    id_: '27',
    name: '双语概率论与随机过程',
    subjectName: '双语概率论与随机过程',
    description: '概率论与随机过程的双语课程，适合结合中英文语境理解随机模型的你',
    color: '#171717',
    teacher: '北邮双语概率团队',
    icon: Dices,
  },
  'number-theory': {
    id: 'number-theory',
    id_: '8',
    name: '数论',
    subjectName: '数论',
    description: '研究整数的奥秘与应用，适合喜欢逻辑和证明的你',
    color: '#171717',
    teacher: '北邮数论团队',
    icon: Percent,
  },
  'statistics': {
    id: 'statistics',
    id_: '9',
    name: '统计决策与贝叶斯分析',
    subjectName: '贝叶斯统计',
    description: '数据分析与推断的科学，适合喜欢实用和应用的你',
    color: '#171717',
    teacher: '北邮贝叶斯统计团队',
    icon: ChartLine,
  },
  'set-theory': {
    id: 'set-theory',
    id_: '11',
    name: '集合',
    subjectName: '集合',
    description: '数学思维的起点，适合喜欢清晰分类与逻辑整理的你',
    color: '#171717',
    teacher: '待定',
    icon: CircleSlash2,
  },
  'solid-geometry': {
    id: 'solid-geometry',
    id_: '12',
    name: '立体几何',
    subjectName: '立体几何',
    description: '空间想象的舞台，适合喜欢构造与空间探索的你',
    color: '#171717',
    teacher: '待定',
    icon: Cuboid,
  },
  'analytic-geometry': {
    id: 'analytic-geometry',
    id_: '13',
    name: '解析几何',
    subjectName: '解析几何',
    description: '几何图形与代数方程的对话，适合喜欢数形结合的你',
    color: '#171717',
    teacher: '待定',
    icon: Axis3D,
  },
  'trigonometry': {
    id: 'trigonometry',
    id_: '14',
    name: '三角函数',
    subjectName: '三角函数',
    description: '周期与不同波形的世界，适合喜欢规律与变化的你',
    color: '#171717',
    teacher: '待定',
    icon: AudioWaveform,
  },
  'plane-vectors': {
    id: 'plane-vectors',
    id_: '15',
    name: '平面向量',
    subjectName: '平面向量',
    description: '方向与数量的统一，适合喜欢简洁表达与几何建模的你',
    color: '#171717',
    teacher: '待定',
    icon: MoveUpRight,
  },
  'functions': {
    id: 'functions',
    id_: '16',
    name: '函数',
    subjectName: '函数',
    description: '变化与关系的核心，适合喜欢抽象思维与模型构建的你',
    color: '#171717',
    teacher: '待定',
    icon: LineChart,
  },
  'logical-reasoning': {
    id: 'logical-reasoning',
    id_: '18',
    name: '逻辑用语',
    subjectName: '逻辑用语',
    description: '推理与论证的工具，适合喜欢严谨思维和逻辑推理的你',
    color: '#171717',
    teacher: '待定',
    icon: CheckSquare,
  },
  'sequences-series': {
    id: 'sequences-series',
    id_: '19',
    name: '数列',
    subjectName: '数列',
    description: '规律与递推的艺术，适合喜欢寻找规律与模式的你',
    color: '#171717',
    teacher: '待定',
    icon: ListOrdered,
  },
  'derivatives': {
    id: 'derivatives',
    id_: '20',
    name: '导数',
    subjectName: '导数',
    description: '变化率的数学语言，适合喜欢分析趋势与优化问题的你',
    color: '#171717',
    teacher: '待定',
    icon: Tangent,
  },
  'probability-statistics': {
    id: 'probability-statistics',
    id_: '21',
    name: '概率统计',
    subjectName: '概率统计',
    description: '数据分析与推断的科学，适合喜欢实用和应用的你',
    color: '#171717',
    teacher: '待定',
    icon: Percent,
  },
  'risk-theory': {
    id: 'risk-theory',
    id_: '23',
    name: '风险理论',
    subjectName: '风险理论',
    description: '风险分析与统计推断的核心，适合注重实用与推导的你',
    color: '#171717',
    teacher: '北邮风险理论团队',
    icon: ShieldCheck,
  },
} as const;

interface HomeConfig {
  /** 是否在课程首页和详情页展示“每周一思”模块。未填写时兼容旧配置：有内容即展示。 */
  showWeeklyThought?: boolean;
  /** 课程首页和详情页的思考模块标题。 */
  weeklyThoughtLabel?: string;
  experiments: {
    title: string;
    description: string;
    img: string;
    path: string;
  }[];
  mindMaps: {
    title: string;
    description: string;
    img: string;
    path: string;
  }[];
  thinking: {
    title: string;
    description: string[];
    docxUrl?: string;
    pdfUrl?: string;
    /** 可选跳转目标；适用于后续思想对谈等新内容形态。 */
    route?: string;
    /** 思想对谈的显示身份与首个引导问题。 */
    persona?: string;
    prompt?: string;
  }[];
}

interface DrawerItem {
  title: string;
  icon: string;
  children?: DrawerItem[];
  route?: string;
  chapterId?: string;
}

interface ExperimentConfig {
  experiments: DrawerItem[];
}

const homeConfigs: Record<SubjectId, HomeConfig> = {
  'probability': probabilityHomeConfig,
  'probability-bilingual': {
    experiments: [],
    mindMaps: [],
    thinking: [],
  },
  'advanced-math-1': advancedMath1HomeConfig,
  'advanced-math-2': advancedMath2HomeConfig,
  'statistics': statisticsHomeConfig,
  'linear-algebra': linearAlgebraHomeConfig,
  'number-theory': numberTheoryHomeConfig,
  'set-theory': setTheoryHomeConfig,
  'solid-geometry': solidGeometryHomeConfig,
  'analytic-geometry': analyticGeometryHomeConfig,
  'trigonometry': trigonometryHomeConfig,
  'plane-vectors': planeVectorsHomeConfig,
  'functions': functionsHomeConfig,
  'logical-reasoning': logicalReasoningHomeConfig,
  'sequences-series': sequencesSeriesHomeConfig,
  'derivatives': derivativesHomeConfig,
  'probability-statistics': probabilityStatisticsHomeConfig,
  'risk-theory': riskTheoryHomeConfig,
  'linear-algebra-en': linearAlgebraEnHomeConfig,
};

const experimentConfigs: Record<SubjectId, ExperimentConfig> = {
  'probability': probabilityExperimentConfig,
  'probability-bilingual': {
    experiments: [],
  },
  'advanced-math-1': advancedMath1ExperimentConfig,
  'advanced-math-2': advancedMath2ExperimentConfig,
  'statistics': statisticsExperimentConfig,
  'linear-algebra': linearAlgebraExperimentConfig,
  'number-theory': numberTheoryExperimentConfig,
  'set-theory': setTheoryExperimentConfig,
  'solid-geometry': solidGeometryExperimentConfig,
  'analytic-geometry': analyticGeometryExperimentConfig,
  'trigonometry': trigonometryExperimentConfig,
  'plane-vectors': planeVectorsExperimentConfig,
  'functions': functionsExperimentConfig,
  'logical-reasoning': logicalReasoningExperimentConfig,
  'sequences-series': sequencesSeriesExperimentConfig,
  'derivatives': derivativesExperimentConfig,
  'probability-statistics': probabilityStatisticsExperimentConfig,
  'risk-theory': riskTheoryExperimentConfig,
  'linear-algebra-en': linearAlgebraEnExperimentConfig,
};

export type { DrawerItem, HomeConfig, Subject, SubjectId };
export { experimentConfigs, homeConfigs, subjectConfig };
